import React, { useEffect, useState } from 'react';
import {
    TextField, Button, Typography, Box, List, ListItem, ListItemText, IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import api from '../../../conf/api';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { toast } from 'react-toastify';
import Scrollbars from 'react-custom-scrollbars-2';

const CategoryManagerDialog = ({ open, handleClose }) => {
    const [categories, setCategories] = useState([]);
    const [newCategory, setNewCategory] = useState('');
    const [loading, setLoading] = useState(false);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [categoryToDelete, setCategoryToDelete] = useState(null);

    const fetchCategories = async () => {
        setLoading(true);
        try {
            const response = await api.get('/users/categories/');
            setCategories(response.data);
        } catch (err) {
            console.error(err);
        }
        setLoading(false);
    };

    const handleAddCategory = async () => {
        if (!newCategory.trim()) return;
        try {
            const response = await api.post('/users/categories/', {
                name: newCategory,
            });
            setCategories([...categories, response.data]);
            setNewCategory('');
        } catch (err) {
            console.error(err);
            if (err.response && err.response.status === 400 && err.response.data.name) {
                toast.error(err.response.data.name[0]);
            } else {
                toast.error('Error adding category');
            }
        }
    };

    const confirmDeleteCategory = (id) => {
        setCategoryToDelete(id);
        setConfirmDialogOpen(true);
    };

    const handleConfirmDelete = async () => {
        try {
            setLoading(true);
            await api.delete(`/users/categories/${categoryToDelete}/`);
            setCategories(categories.filter((cat) => cat.id !== categoryToDelete));
        } catch (err) {
            console.error(err);
            alert('Error deleting category');
        } finally {
            setConfirmDialogOpen(false);
            setCategoryToDelete(null);
            setLoading(false);
        }
    };

    const handleCancelDelete = () => {
        setConfirmDialogOpen(false);
        setCategoryToDelete(null);
    };

    useEffect(() => {
        fetchCategories();
    }, [open]);

    return (
        <>
            <Dialog open={open} onClose={handleClose} maxWidth={300}>
                <DialogTitle>
                    <Typography variant="h5" gutterBottom>
                        Manage Creator Categories
                    </Typography>
                </DialogTitle>

                <DialogContent sx={{ minWidth: 600 }}>
                    <Box display="flex" gap={2} mb={3}>
                        <TextField
                            placeholder="New Category"
                            variant="outlined"
                            size='large'
                            fullWidth
                            value={newCategory}
                            onChange={(e) => setNewCategory(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    handleAddCategory();
                                }
                            }}
                            disabled={loading}
                        />
                        <Button
                            sx={{ minWidth: 100 }}
                            size="large"
                            variant="contained"
                            color="primary"
                            onClick={handleAddCategory}
                            disabled={loading}
                        >
                            Add
                        </Button>
                    </Box>
                    <Typography variant="h6">Existing Categories:</Typography>
                    <Scrollbars style={{ height: categories.length === 0 ? 50 : 200 }}>
                        <List>
                            {categories.length < 1 &&
                                <Box display="flex" alignItems="center" flexDirection="column" gap={.5}>
                                    <VisibilityOffIcon fontSize='large' color="disabled" />
                                    <Typography variant='subtitle1' >No categories added</Typography>
                                </Box>
                            }

                            {categories.map((category) => (
                                <ListItem
                                    key={category.id}
                                    secondaryAction={
                                        <IconButton disabled={loading} edge="end" onClick={() => confirmDeleteCategory(category.id)}>
                                            <DeleteIcon color="error" />
                                        </IconButton>
                                    }
                                >
                                    <ListItemText primary={category.name} />
                                </ListItem>
                            ))}
                        </List>
                    </Scrollbars>
                </DialogContent>
            </Dialog>

            {/* Confirm Deletion Dialog */}
            <Dialog open={confirmDialogOpen} onClose={handleCancelDelete}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <Typography>Are you sure you want to delete this category?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelDelete} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={handleConfirmDelete} color="error" variant="contained">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default CategoryManagerDialog;
